import React from 'react';
import { graphql, useFragment } from 'react-relay';

import ExternalResourceIcon from 'topics/src/common/ui/external-resource-fallback-icon';
import { NewTabTooltipContainer } from 'topics/src/common/ui/new-tab-tooltip-container';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import BookIcon from '@atlaskit/icon/glyph/book';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { AutoScaledImage } from '@atlassian/help-center-common-component/auto-scaled-image';
import { urlRefsAndTarget, isExternalResource } from '@atlassian/help-center-common-util/is-external-resource';
import { origin } from '@atlassian/help-center-common-util/location';
import type { helpObject$key as HelpCenterCollectionItemType } from './__generated__/helpObject.graphql';
import type { helpObjectRequestForm$key as HelpObjectRequestFormType } from './__generated__/helpObjectRequestForm.graphql';
import type { helpObjectStoreArticle$key as HelpObjectStoreArticleType } from './__generated__/helpObjectStoreArticle.graphql';
import type { helpObjectStoreChannel$key as HelpObjectChannelType } from './__generated__/helpObjectStoreChannel.graphql';
import { StyledHelpObject, Title, ImageContainer, StyledLink } from './styled';
import { HelpObjectTypes } from './types';

interface Props {
    helpObject: HelpCenterCollectionItemType | null;
    readOnly?: boolean;
}

interface LinkOptions {
    isUnLinked?: boolean;
}

const StyledLinkWithProps = ({
    displayLink,
    title,
    isAnExternalResource = false,
}: {
    displayLink: string;
    title: string;
    isAnExternalResource?: boolean;
}) => (
    <StyledLink actionSubjectId="helpObject" {...urlRefsAndTarget({ displayLink, isAnExternalResource })}>
        <Title>{title}</Title>
    </StyledLink>
);

/*
    Please note that topic creation is only stored in the local store
    and not persisted on the server until the final publication.
    Hence, any changes made to this query must be reflected in the
    local store as well.
*/

export const HelpObjectRequestForm = ({
    helpObject,
    isUnLinked,
}: { helpObject: HelpObjectRequestFormType } & LinkOptions) => {
    // TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
    const { title, icon, displayLink, entityKey } = useFragment<HelpObjectRequestFormType>(
        graphql`
            fragment helpObjectRequestForm on HelpObjectStoreRequestForm {
                title
                displayLink
                icon {
                    iconUrlV2
                }
                entityKey
            }
        `,
        helpObject
    );

    const getImageUrl = () => {
        if (!icon) {
            return undefined;
        }

        return new URL(icon.iconUrlV2, origin()).toString();
    };

    const imageUrl = getImageUrl();
    const isAnExternalResource = isExternalResource(entityKey as string);
    return (
        <>
            <ImageContainer>
                {isAnExternalResource ? (
                    <ExternalResourceIcon size="medium" iconUrl={displayLink} resourceType="REQUEST_FORM" />
                ) : (
                    imageUrl && <AutoScaledImage src={imageUrl} alt="" />
                )}
            </ImageContainer>
            {isUnLinked ? (
                <Title showRegularCursor={isUnLinked}>{title}</Title>
            ) : (
                <NewTabTooltipContainer shouldOpenInANewTab={isAnExternalResource}>
                    <StyledLinkWithProps
                        displayLink={displayLink as string}
                        title={title as string}
                        isAnExternalResource={isAnExternalResource}
                    />
                </NewTabTooltipContainer>
            )}
        </>
    );
};
export const HelpObjectStoreArticle = ({
    helpObject,
    isUnLinked,
}: { helpObject: HelpObjectStoreArticleType } & LinkOptions) => {
    const { title, displayLink, entityKey } = useFragment<HelpObjectStoreArticleType>(
        graphql`
            fragment helpObjectStoreArticle on HelpObjectStoreArticle {
                title
                displayLink
                entityKey
            }
        `,
        helpObject
    );
    const isAnExternalResource = isExternalResource(entityKey as string);
    return (
        <>
            <ImageContainer>
                {isAnExternalResource ? (
                    <ExternalResourceIcon size="medium" iconUrl={displayLink} resourceType="ARTICLE" />
                ) : (
                    <BookIcon primaryColor={token('color.icon', colors.N500)} label="helpObjectArticle" size="medium" />
                )}
            </ImageContainer>
            {isUnLinked ? (
                <Title showRegularCursor={isUnLinked}>{title}</Title>
            ) : (
                <NewTabTooltipContainer shouldOpenInANewTab={isAnExternalResource}>
                    <StyledLinkWithProps
                        displayLink={displayLink as string}
                        title={title as string}
                        isAnExternalResource={isAnExternalResource}
                    />
                </NewTabTooltipContainer>
            )}
        </>
    );
};
export const HelpObjectStoreChannel = ({
    helpObject,
    isUnLinked,
}: { helpObject: HelpObjectChannelType } & LinkOptions) => {
    const { title, displayLink } = useFragment<HelpObjectChannelType>(
        graphql`
            fragment helpObjectStoreChannel on HelpObjectStoreChannel {
                title
                displayLink
            }
        `,
        helpObject
    );
    return (
        <>
            <ImageContainer>
                <ExternalResourceIcon size="medium" iconUrl={displayLink} resourceType="CHANNEL" />
            </ImageContainer>
            {isUnLinked ? (
                <Title showRegularCursor={isUnLinked}>{title}</Title>
            ) : (
                <NewTabTooltipContainer shouldOpenInANewTab>
                    <StyledLinkWithProps
                        displayLink={displayLink as string}
                        title={title as string}
                        isAnExternalResource
                    />
                </NewTabTooltipContainer>
            )}
        </>
    );
};

const HelpObject = (props: Props) => {
    const data = useFragment<HelpCenterCollectionItemType>(
        graphql`
            fragment helpObject on HelpCenterTopicItemEdge {
                node {
                    data {
                        ... on HelpCenterHelpObject {
                            __typename
                            ... on HelpObjectStoreRequestForm {
                                ...helpObjectRequestForm
                                entityKey
                            }

                            ... on HelpObjectStoreArticle {
                                ...helpObjectStoreArticle
                                entityKey
                            }

                            ... on HelpObjectStoreChannel {
                                ...helpObjectStoreChannel
                                entityKey
                            }
                            ... on HelpObjectStoreQueryError {
                                __typename
                            }
                        }
                    }
                }
            }
        `,
        props.helpObject
    );
    const node = data?.node;
    if (node == null) {
        return null;
    }
    const helpObject = node.data ?? null;
    const type = helpObject?.__typename;
    if (helpObject === null || type === HelpObjectTypes.HelpObjectStoreQueryError) {
        return null;
    }
    let entityKey;
    if (
        data?.node?.data?.__typename === 'HelpObjectStoreArticle' ||
        data?.node?.data?.__typename === 'HelpObjectStoreRequestForm' ||
        data?.node?.data?.__typename === 'HelpObjectStoreChannel'
    ) {
        entityKey = data.node.data.entityKey || '';
    }
    const isExternal = isExternalResource(entityKey as string);

    return (
        <AnalyticsContext data={{ attributes: { type, isExternal } }}>
            <StyledHelpObject>
                {type === HelpObjectTypes.HelpObjectStoreArticle ? (
                    <HelpObjectStoreArticle helpObject={helpObject} isUnLinked={props.readOnly} />
                ) : null}
                {type === HelpObjectTypes.HelpObjectStoreRequestForm ? (
                    <HelpObjectRequestForm helpObject={helpObject} isUnLinked={props.readOnly} />
                ) : null}
                {type === HelpObjectTypes.HelpObjectStoreChannel ? (
                    <HelpObjectStoreChannel helpObject={helpObject} isUnLinked={props.readOnly} />
                ) : null}
            </StyledHelpObject>
        </AnalyticsContext>
    );
};

export default HelpObject;
